import { isDefined } from "@clipboard-health/util-ts";
import { ACTIVE_CHARTING_NONCOMPLIANCE_STATES } from "@src/appV2/redesign/Charting/constants";
import { type UseQueryOptions } from "@tanstack/react-query";

import { chartingDeadlineHasPassed } from "../utils/helpers";
import { type GetChartingNoncompliancesResponse } from "./schema";
import { useGetChartingNoncompliances } from "./useGetChartingNoncompliances";

interface GetMostRelevantChartingNoncomplianceParams {
  workerId: string;
}

interface MostRelevantChartingNoncomplianceResult {
  chartingNoncompliance: GetChartingNoncompliancesResponse["data"][0] | undefined;
  noncomplianceExists: boolean;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  isInReview: boolean;
  isInViolation: boolean;
}

function mostRelevantChartingNoncompliance(
  chartingNoncompliances: GetChartingNoncompliancesResponse["data"]
) {
  const sortedChartingNoncompliances = chartingNoncompliances.sort((a, b) => {
    // The chartingNoncompliances should already be sorted by deadlineToChart,
    // but in case that is not the case, we sort them by deadlineToChart to get
    // the noncompliance expiring soonest as "most relevant"
    return (
      new Date(a.attributes.deadlineToChart).getTime() -
      new Date(b.attributes.deadlineToChart).getTime()
    );
  });

  return sortedChartingNoncompliances[0];
}

/**
 * Custom hook that gets the most relevant charting noncompliance for a worker.
 *
 * This hook wraps useGetChartingNoncompliances and extracts the most relevant noncompliance
 * along with a boolean indicating if a noncompliance exists.
 *
 * @param params - Parameters for fetching charting noncompliances
 * @param options - Additional options for the underlying useQuery
 * @returns Object containing the most relevant noncompliance, existence flag, and query states
 */
export function useGetMostRelevantChartingNoncompliance(
  params: GetMostRelevantChartingNoncomplianceParams,
  options: UseQueryOptions<GetChartingNoncompliancesResponse> = {}
): MostRelevantChartingNoncomplianceResult {
  const { workerId } = params;

  const {
    data: chartingNoncompliances,
    isLoading,
    isError,
    isSuccess,
  } = useGetChartingNoncompliances(
    { workerId, state: ACTIVE_CHARTING_NONCOMPLIANCE_STATES },
    { enabled: isDefined(workerId), ...options }
  );

  const chartingNoncompliance = mostRelevantChartingNoncompliance(
    chartingNoncompliances?.data ?? []
  );

  const noncomplianceExists = isSuccess && isDefined(chartingNoncompliance);
  const isInReview =
    isDefined(chartingNoncompliance?.attributes.deadlineToReview) &&
    !chartingDeadlineHasPassed(chartingNoncompliance?.attributes.deadlineToReview);

  const isInViolation =
    !isInReview && chartingDeadlineHasPassed(chartingNoncompliance?.attributes.deadlineToChart);

  return {
    chartingNoncompliance,
    noncomplianceExists,
    isInReview,
    isInViolation,
    isLoading,
    isError,
    isSuccess,
  };
}
